/**
 * Dependencies.
 */
import { ServicePointDeliveryModel } from '@/api/service-point/delivery/delivery.model';
import { ServicePointOrderModel } from '@/api/service-point/order/order.model';
import applicationConstant from '@/modules/main/constants/application.constant';
import { BaseService } from '@/modules/main/services/base.service';

import { ServicePointModel } from './service-point.model';

/**
 * Interface `IOrdersTotal`.
 */
export interface IOrdersTotal {
  collectPackagesToReceive: number;
  collectsToReceive: number;
  ordersCanceled: number;
  ordersFinished: number;
  ordersInServicePoint: number;
  ordersToReceive: number;
  packagesFinished: number;
  packagesInServicePoint: number;
  packagesToReceive: number;
}

/**
 * Service.
 */
class ServicePointService extends BaseService<ServicePointModel> {
  /**
   * Constructor.
   */
  constructor() {
    super(
      ServicePointModel,
      ServicePointModel.name,
      applicationConstant.apiServicePointPrivateUrl
    );
  }

  /**
   * Get deliveries.
   */
  getDeliveries(): Promise<ServicePointDeliveryModel[]> {
    const endpoint = `${this.getModelEndpoint}/deliveries?_limit=9999999`;

    return this.GET(endpoint);
  }

  /**
   * Get services points from user.
   */
  getFromUser({
    limit = 10,
    page = 1,
    searchString = ''
  }: {
    limit?: number;
    page?: number;
    searchString?: string;
  } = {}): Promise<ServicePointModel[]> {
    let params = `?_limit=${limit}&_page=${page}`;

    if (searchString) {
      params = params + `&name=${encodeURIComponent(searchString)}`;
    }

    const endpoint = `${this.getModelEndpoint}/fromUser${params}`;

    return this.GET(endpoint);
  }

  /**
   * Get orders.
   */
  getOrders({ limit = 9999999, page = 1, status = null } = {}): Promise<
  ServicePointOrderModel[]
  > {
    let endpoint = `${this.getModelEndpoint}/orders?_limit=${limit}&_page=${page}`;

    if (status) {
      endpoint = `${endpoint}&status=${status}`;
    }

    return this.GET(endpoint);
  }

  /**
   * Get orders by status code.
   */
  getOrdersByStatusCode(code: string): Promise<ServicePointOrderModel[]> {
    const endpoint = `${this.getModelEndpoint}/orders?_limit=9999999&status=${code}`;

    return this.GET(endpoint);
  }

  /**
   * Get orders totals.
   */
  getOrdersTotals(): Promise<IOrdersTotal> {
    const endpoint = `${this.getModelEndpoint}/totalNumberOrders`;

    return this.GET(endpoint);
  }
}

/**
 * Export `servicePointService`.
 */
export const servicePointService = new ServicePointService();
